import { FC, PropsWithChildren, useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';

import { DASHBOARD_SIDEBAR_GROUPS } from './constants';
import {
  type DashboardSidebarFilterOptions,
  filterDashboardSidebarGroups,
} from './filterDashboardSidebarGroups';
import { GlobalAuthenticatedBanners } from './GlobalAuthenticatedBanners';
import { HeaderMobile } from './HeaderMobile';
import { useAuth } from '@workos-inc/authkit-react';

import { useLogout } from '@/auth/useLogout';
import { useViewerLeaderInsightsAccess } from '@/auth/useViewerLeaderInsightsAccess';
import { useViewerProfile } from '@/auth/useViewerProfile';
import { OrganizationSidebarBrand } from '@/components';
import { DashboardSidebar, SidebarProvider } from '@/components/organisms';
import { RoutesEnum } from '@/enums';
import { useOrganizationLogoUrl } from '@/hooks/useOrganizationLogoUrl';
import { useGetOrganizationQuery } from '@/store/api/organization';
import { useFeatureFlags } from '@/utils/featureFlags';

const BLOCKS_SIDEBAR_PAGES: string[] = [RoutesEnum.DATA_EXPLORER_BUILDER];

type MainLayoutProps = PropsWithChildren<unknown>;

export const MainLayout: FC<MainLayoutProps> = ({ children }) => {
  const location = useLocation();
  const { user } = useAuth();
  const logout = useLogout();
  const { role, displayName, jobTitle, profileImageUrl, isProfileLoading } = useViewerProfile();
  const { canAccessLeaderInsights, isLeaderInsightsAccessResolved } =
    useViewerLeaderInsightsAccess();
  const featureFlags = useFeatureFlags();
  const { data: organizationData, isLoading: isOrganizationLoading } = useGetOrganizationQuery();
  const setupComplete = organizationData?.setupStatus?.complete === true;
  const isNavigationLoading = isOrganizationLoading || !isLeaderInsightsAccessResolved;
  const sidebarGroups = useMemo(() => {
    if (isNavigationLoading) {
      return [];
    }
    const sidebarFilterOptions: DashboardSidebarFilterOptions = {
      hideGettingStarted: setupComplete,
      role,
      showLeaderInsights: canAccessLeaderInsights,
    };
    return filterDashboardSidebarGroups(
      DASHBOARD_SIDEBAR_GROUPS,
      featureFlags,
      sidebarFilterOptions
    );
  }, [featureFlags, isNavigationLoading, role, setupComplete, canAccessLeaderInsights]);
  const logoUrl = useOrganizationLogoUrl();
  const headerLogoTo =
    !isOrganizationLoading && setupComplete
      ? RoutesEnum.DAILY_SURVEY_OVERVIEW
      : RoutesEnum.GET_STARTED;
  if (!user) return null;

  const shouldShowBlocksSidebar = BLOCKS_SIDEBAR_PAGES.includes(location.pathname);

  return (
    <div className="flex h-full w-full">
      <SidebarProvider>
        <aside>
          {!shouldShowBlocksSidebar && (
            <DashboardSidebar
              isNavigationLoading={isNavigationLoading}
              isUserProfileLoading={isProfileLoading}
              onLogout={() => {
                void logout();
              }}
              header={
                <Link
                  to={headerLogoTo}
                  aria-label="Go to dashboard"
                  className="flex h-10 w-full min-w-0 items-center justify-start"
                >
                  <OrganizationSidebarBrand
                    organizationName={organizationData?.name ?? ''}
                    logoUrl={logoUrl}
                    logoAlt={
                      organizationData?.name?.trim()
                        ? `${organizationData.name.trim()} logo`
                        : undefined
                    }
                  />
                </Link>
              }
              groups={sidebarGroups}
              user={{
                image: profileImageUrl ?? user.profilePictureUrl ?? undefined,
                jobTitle: jobTitle?.trim() || undefined,
                name: displayName || `${user.firstName} ${user.lastName}`.trim(),
              }}
            />
          )}
        </aside>
        <main className="flex flex-1 flex-col min-h-0 h-full min-w-0">
          <HeaderMobile />
          <GlobalAuthenticatedBanners />
          {children}
        </main>
      </SidebarProvider>
    </div>
  );
};
